import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Grid, TextField, Typography, Box } from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";

import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";

import { useAuth } from "hooks";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import {
    useLazyGetDriverDetailForRoutineQuery,
} from "store/services/routeDepartureVehicleApi";
import AppSwitch from "components/Shared/AppSwitch";

const DAYS_OF_WEEK = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const INITIAL_STATE = {
    account: null,
    entity: null,
    driver: null,
    vehicle: null,
    isActive: true,
};

const RouteVehicleDepartureForm = ({
    isBusy,
    departure,
    routeId,
    onSubmit,
    onCancel,
}) => {
    const { isAccountAdmin, isSystemAdmin, viewEntity } = useAuth();

    // State for managing day schedules
    const [daySchedules, setDaySchedules] = useState(
        DAYS_OF_WEEK.reduce((acc, day) => {
            acc[day] = "";
            return acc;
        }, {})
    );

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: {
            ...INITIAL_STATE,
            entity: viewEntity?.entity,
            account: viewEntity?.entity?.account,
        },
    });

    const _watchAccount = useWatch({ control, name: "account" });
    const _watchDriver = useWatch({ control, name: "driver" });

    const { data: accounts = [], isLoading: loadingAccounts } =
        useGetDDLAccountListQuery();
    const [fetchEntities, { data: entities = [], isLoading: loadingEntities }] =
        useLazyGetDDLEntityListQuery();

    const [
        fetchDriverDetails,
        { data: driversData, isLoading: loadingDriverDetails },
    ] = useLazyGetDriverDetailForRoutineQuery();

    const drivers = useMemo(() => {
        return Array.isArray(driversData)
            ? driversData.map(item => item.driver).filter(Boolean)
            : [];
    }, [driversData]);

    const selectedDriverVehicles = _watchDriver?.assigned_vehicle_ids || [];

    useEffect(() => {
        if (isAccountAdmin || isSystemAdmin) {
            fetchEntities({
                account: _watchAccount?._id ?? viewEntity?.entity?.account?._id,
            });
        }
    }, [
        _watchAccount?._id,
        fetchEntities,
        isAccountAdmin,
        isSystemAdmin,
        viewEntity?.entity?.account?._id,
    ]);

    useEffect(() => {
        if (routeId) {
            fetchDriverDetails(routeId);
        }
    }, [routeId, fetchDriverDetails]);

    const hasResetRef = useRef(false);
    useEffect(() => {
        if (hasResetRef.current) return;

        if (departure && drivers.length > 0) {
            const driverMatch = drivers.find(d => d._id === departure.driver?._id);
            const vehicleMatch = driverMatch?.assigned_vehicle_ids?.find(
                v => v._id === departure.vehicle?._id
            );

            reset({
                account: driverMatch?.account || viewEntity?.entity?.account,
                entity: driverMatch?.entity || viewEntity?.entity,
                driver: driverMatch || null,
                vehicle: vehicleMatch || null,
                isActive: departure.isActive ?? true,
            });

            // Set day schedules from existing departure
            if (departure.schedules && Array.isArray(departure.schedules)) {
                const newDaySchedules = DAYS_OF_WEEK.reduce((acc, day) => {
                    const schedule = departure.schedules.find(s => s.day === day);
                    acc[day] = schedule?.departureTime || "";
                    return acc;
                }, {});
                setDaySchedules(newDaySchedules);
            }

            hasResetRef.current = true;
        } else if (!departure && drivers.length > 0) {
            reset({
                ...INITIAL_STATE,
                entity: viewEntity?.entity,
                account: viewEntity?.entity?.account,
            });
            // Reset day schedules
            setDaySchedules(
                DAYS_OF_WEEK.reduce((acc, day) => {
                    acc[day] = "";
                    return acc;
                }, {})
            );
            hasResetRef.current = true;
        }
    }, [departure, drivers, reset, viewEntity?.entity]);

    const handleDayToggle = (day) => {
        setDaySchedules(prev => ({
            ...prev,
            [day]: {
                ...prev[day],
                enabled: !prev[day].enabled,
                time: !prev[day].enabled ? prev[day].time : "",
            },
        }));
    };

    const handleTimeChange = (day, time) => {
        setDaySchedules(prev => ({
            ...prev,
            [day]: time,
        }));
    };

    const handleFormSubmit = async (formValues) => {
        if (!formValues.driver || !formValues.vehicle) {
            toast.error("Driver and vehicle are required!");
            return;
        }

        // Build schedules array from daySchedules
        const schedules = DAYS_OF_WEEK.filter(day => daySchedules[day])
            .map(day => ({
                day,
                departureTime: daySchedules[day],
            }));

        if (schedules.length === 0) {
            toast.error("Please select at least one day with a departure time!");
            return;
        }

        const payload = {
            account: formValues.account._id,
            entity: formValues.entity._id,
            route: routeId,
            driver: formValues.driver._id,
            vehicle: formValues.vehicle._id,
            schedules,
            isActive: formValues.isActive,
        };

        onSubmit(payload);
    };

    return (
        <AppForm loading={isBusy || loadingDriverDetails}>
            <AppForm.Content>
                <Grid container spacing={2}>
                    {(isSystemAdmin || isAccountAdmin) && (
                        <>
                            <Grid item md={12}>
                                <Controller
                                    name="account"
                                    control={control}
                                    rules={{ required: "Please select an account." }}
                                    render={({ field }) => (
                                        <BaseAutocomplete
                                            {...field}
                                            fullWidth
                                            label="Account"
                                            required
                                            disabled
                                            value={field.value ?? ""}
                                            onChange={(_, data) => {
                                                if (data?._id !== field.value?._id) {
                                                    field.onChange(data);
                                                    setValue("entity", "");
                                                }
                                            }}
                                            getOptionLabel={(option) => option?.name ?? ""}
                                            isOptionEqualToValue={(option, value) =>
                                                option?._id === value?._id
                                            }
                                            options={accounts}
                                            loading={loadingAccounts}
                                            helperText={errors?.account?.message}
                                            error={!!errors?.account}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item md={12}>
                                <Controller
                                    name="entity"
                                    control={control}
                                    rules={{ required: "Please select an entity." }}
                                    render={({ field }) => (
                                        <BaseAutocomplete
                                            {...field}
                                            fullWidth
                                            label="Entity"
                                            required
                                            disabled
                                            value={field.value ?? ""}
                                            onChange={(_, data) => {
                                                field.onChange(data);
                                            }}
                                            getOptionLabel={(option) => option?.name ?? ""}
                                            isOptionEqualToValue={(option, value) =>
                                                option?._id === value?._id
                                            }
                                            options={entities}
                                            loading={loadingEntities}
                                            helperText={errors?.entity?.message}
                                            error={!!errors?.entity}
                                        />
                                    )}
                                />
                            </Grid>
                        </>
                    )}

                    <Grid item md={12}>
                        <Controller
                            name="driver"
                            control={control}
                            rules={{ required: "Please select a driver." }}
                            render={({ field: { value, onChange }, fieldState: { error } }) => (
                                <BaseAutocomplete
                                    fullWidth
                                    label="Driver"
                                    required
                                    value={value}
                                    onChange={(_, data) => {
                                        onChange(data);
                                        setValue("vehicle", null);
                                    }}
                                    getOptionLabel={(option) => option?.name ?? ""}
                                    isOptionEqualToValue={(option, value) =>
                                        option?._id === value?._id
                                    }
                                    options={drivers}
                                    loading={loadingDriverDetails}
                                    helperText={error?.message || "Select a driver assigned to this route"}
                                    error={!!error}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item md={12}>
                        <Controller
                            name="vehicle"
                            control={control}
                            rules={{ required: "Please select a vehicle." }}
                            render={({ field: { value, onChange }, fieldState: { error } }) => (
                                <BaseAutocomplete
                                    fullWidth
                                    label="Vehicle"
                                    required
                                    value={value}
                                    onChange={(_, data) => onChange(data)}
                                    getOptionLabel={(option) =>
                                        option
                                            ? `${option.vehicle_id} - ${option.registration_number}`
                                            : ""
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option?._id === value?._id
                                    }
                                    options={selectedDriverVehicles}
                                    loading={loadingDriverDetails}
                                    helperText={
                                        error?.message ||
                                        (!_watchDriver
                                            ? "Please select a driver first"
                                            : selectedDriverVehicles.length === 0
                                                ? "No vehicles assigned to this driver"
                                                : "Select from driver's assigned vehicles")
                                    }
                                    error={!!error}
                                    disabled={!_watchDriver || selectedDriverVehicles.length === 0}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                            Weekly Schedule
                        </Typography>
                        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
                            {DAYS_OF_WEEK.map((day) => (
                                <Box
                                    key={day}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        mb: 2,
                                        pb: 2,
                                        borderBottom: day !== 'Saturday' ? '1px solid #f0f0f0' : 'none',
                                    }}
                                >
                                    <Typography sx={{ minWidth: 140, fontWeight: 500 }}>
                                        {day}
                                    </Typography>
                                    <TextField
                                        type="time"
                                        value={daySchedules[day]}
                                        onChange={(e) => handleTimeChange(day, e.target.value)}
                                        size="small"
                                        sx={{ flex: 1 }}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        placeholder="Select time"
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Grid>
                    {/* Is Active */}
                    <Grid item xs={6}>
                        <Controller
                            name="isActive"
                            control={control}
                            render={({ field }) => (
                                <AppSwitch {...field} label="Is Active?" />
                            )}
                        />
                    </Grid>
                </Grid>
            </AppForm.Content>
            <AppForm.Actions>
                <Button onClick={onCancel} disabled={isBusy}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit(handleFormSubmit)}
                    disabled={isBusy || loadingDriverDetails}
                >
                    {departure ? "Update" : "Add"}
                </Button>
            </AppForm.Actions>
        </AppForm>
    );
};

export default RouteVehicleDepartureForm;